import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Shield, 
  TrendingUp, 
  LayoutGrid, 
  CheckCircle2, 
  ArrowRight, 
  Mail, 
  Phone, 
  MapPin,
  Users,
  Handshake,
  Clock,
  Calendar,
  MessageCircle,
  Target,
  Compass,
  X
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "./components/ui/dialog";
import { Toaster, toast } from "sonner";
import CookieBanner from "./components/CookieBanner";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_LINK = "https://calendly.com/curafina/neues-meeting";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_finanz-struktur/artifacts/qec9vi2a_ChatGPT%20Image%2020.%20Sept.%202025%2C%2018_48_48.png";
const PORTRAIT_URL = "https://customer-assets.emergentagent.com/job_8b4be372-0051-422e-942f-9ff85c103f6c/artifacts/9fi0y5nc_Design%20ohne%20Titel.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Section component with scroll animation
const AnimatedSection = ({ children, className = "", id = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="section-container py-4 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="CM Consulting Logo" 
            className="h-12 w-auto"
          />
          <span className="font-heading text-xl font-medium text-foreground hidden sm:block">
            CM Consulting
          </span>
        </a>
        
        <div className="hidden md:flex items-center gap-8">
          <a href="#leistungen" className="text-muted-foreground hover:text-primary transition-colors font-medium">
            Leistungen
          </a>
          <a href="#ablauf" className="text-muted-foreground hover:text-primary transition-colors font-medium">
            Ablauf
          </a>
          <a href="#kontakt" className="text-muted-foreground hover:text-primary transition-colors font-medium">
            Kontakt
          </a>
        </div>

        <Button 
          className="btn-primary text-base px-6 py-3"
          onClick={() => window.open(CALENDLY_LINK, "_blank")}
          data-testid="nav-cta-button"
        >
          <Calendar className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Termin buchen</span>
        </Button>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="min-h-screen pt-24 pb-16 flex items-center bg-[#FDFAF7]" data-testid="hero-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-6">
              Freier Versicherungsmakler & Finanzanlagenvermittler
            </p>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-foreground mb-6">
              Finanzen strukturieren. Klar investieren. Langfristig Vermögen aufbauen.
            </h1>
            
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-6">
              Viele wissen, dass sie sich um ihre Finanzen kümmern sollten – aber nicht, wo sie anfangen sollen. 
              Ich unterstütze dich dabei, deine Situation zu ordnen, Prioritäten zu setzen und eine langfristige Strategie aufzubauen.
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Braunschweig vor Ort
              </span>
              <span className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-primary" />
                Bundesweit digital
              </span>
            </div>

            <p className="text-base text-sage-700 bg-sage-50 px-4 py-3 rounded-xl mb-8 border-l-4 border-gold">
              Entscheidungen triffst immer du. Meine Aufgabe ist es, dir Orientierung und eine klare Struktur zu geben.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="btn-primary text-lg px-8 py-6"
                onClick={() => window.open(CALENDLY_LINK, "_blank")}
                data-testid="hero-cta-button"
              >
                Unverbindlich ins Gespräch kommen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mt-4">
              kostenlos • ca. 20–30 Minuten • keine Verpflichtung
            </p>
          </motion.div>
          
          <motion.div 
            className="relative flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative">
              {/* Decorative background elements */}
              <div className="absolute -inset-4 bg-gradient-to-br from-sage-100 via-sage-50 to-transparent rounded-[2rem] transform rotate-2"></div>
              <div className="absolute -inset-2 bg-white/60 rounded-[1.5rem] backdrop-blur-sm"></div>
              
              {/* Portrait image with transparent-friendly styling */}
              <div className="relative z-10 p-4">
                <img 
                  src={PORTRAIT_URL}
                  alt="Christopher Mein - Finanzberater"
                  className="relative w-full max-w-md rounded-2xl object-contain drop-shadow-2xl"
                  style={{ 
                    filter: "drop-shadow(0 25px 50px rgba(74, 103, 88, 0.15))"
                  }}
                  data-testid="portrait-image"
                />
              </div>
              
              {/* Subtle gold accent */}
              <div className="absolute bottom-8 left-8 right-8 h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent rounded-full"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  const trustItems = [
    { icon: Users, text: "Persönlicher Ansprechpartner" },
    { icon: Target, text: "Anbieterübergreifende Beratung" },
    { icon: Handshake, text: "Langfristige Begleitung" },
    { icon: CheckCircle2, text: "Keine Verkaufsverpflichtung" },
  ];

  return (
    <AnimatedSection className="bg-sage-50 py-12" id="trust" data-testid="trust-section">
      <div className="section-container">
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {trustItems.map((item, index) => (
            <motion.div 
              key={index} 
              className="flex flex-col items-center text-center gap-3"
              variants={fadeInUp}
            >
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <span className="text-sm md:text-base font-medium text-sage-700">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Problem Section
const ProblemSection = () => {
  const problems = [
    "Geld liegt ohne klare Strategie auf dem Konto",
    "Unsicherheit bei Finanzentscheidungen",
    "Zu wenig Zeit für strukturierte Planung",
  ];

  return (
    <AnimatedSection className="section-spacing bg-white" id="problem" data-testid="problem-section">
      <div className="section-container">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-foreground mb-10">
            Kommt dir das bekannt vor?
          </h2>
          
          <motion.div 
            className="space-y-4 mb-10"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {problems.map((problem, index) => (
              <motion.div 
                key={index}
                className="flex items-center gap-4 bg-sage-50 p-5 rounded-xl text-left"
                variants={fadeInUp}
              >
                <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-gold font-semibold">{index + 1}</span>
                </div>
                <p className="text-lg text-sage-700">{problem}</p>
              </motion.div>
            ))}
          </motion.div>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Wenn du dich in einem dieser Punkte wiedererkennst, bist du nicht allein – 
            genau hier setzt eine strukturierte Finanzplanung an.
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Solution Section
const SolutionSection = () => {
  const solutions = [
    {
      icon: Shield,
      title: "Absicherung",
      description: "Existenzielle Risiken erkennen und minimieren. Eine solide Basis für deine finanzielle Sicherheit."
    },
    {
      icon: TrendingUp,
      title: "Vermögensaufbau",
      description: "Wissenschaftlich fundierte Investmentstrategien für langfristigen und nachhaltigen Vermögensaufbau."
    },
    {
      icon: LayoutGrid,
      title: "Struktur & Überblick",
      description: "Ordnung in deine Finanzen bringen. Alle Verträge und Anlagen übersichtlich an einem Ort."
    },
  ];

  return (
    <AnimatedSection className="section-spacing bg-[#FDFAF7]" id="leistungen" data-testid="solution-section">
      <div className="section-container">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-foreground mb-6">
            Ganzheitliche Finanzplanung für deine aktuelle Situation
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Statt einzelner Produkte betrachte ich deine gesamte finanzielle Situation und entwickle mit dir einen Plan, 
            der zu deinen Zielen und deiner Lebenssituation passt.
          </p>
        </div>
        
        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {solutions.map((solution, index) => (
            <motion.div 
              key={index}
              className="feature-card hover-lift group"
              variants={fadeInUp}
              data-testid={`solution-card-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-sage-100 flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                <solution.icon className="w-7 h-7 text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-2xl font-medium text-foreground mb-4">{solution.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{solution.description}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <p className="text-center text-muted-foreground mt-12 max-w-2xl mx-auto">
          Ziel ist nicht, Produkte zu verkaufen, sondern eine Struktur zu schaffen, die langfristig zu dir passt.
        </p>
      </div>
    </AnimatedSection>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Unverbindliches Kennenlernen",
      description: "Wir lernen uns kennen und ich verstehe deine aktuelle Situation und deine Ziele. Ohne Druck, ohne Verpflichtung."
    },
    {
      number: "02",
      title: "Analyse deiner aktuellen Situation",
      description: "Gemeinsam schauen wir uns deine bestehenden Verträge, Vermögenswerte und finanziellen Verpflichtungen an."
    },
    {
      number: "03",
      title: "Struktur und individuelle Strategie",
      description: "Basierend auf deinen Zielen entwickle ich einen klaren Plan mit konkreten Handlungsempfehlungen."
    },
    {
      number: "04",
      title: "Umsetzung und langfristige Begleitung",
      description: "Ich begleite dich bei der Umsetzung und stehe dir auch danach als Ansprechpartner zur Verfügung."
    },
  ];

  return (
    <AnimatedSection className="section-spacing bg-white" id="ablauf" data-testid="process-section">
      <div className="section-container">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-foreground mb-6">
            So läuft die Zusammenarbeit ab
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Transparenz und Klarheit von Anfang an. Du entscheidest jederzeit selbst, welche Schritte du gehen möchtest.
          </p>
        </div>
        
        <motion.div 
          className="max-w-3xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, index) => (
            <motion.div 
              key={index}
              className={`relative flex gap-6 pb-12 ${index < steps.length - 1 ? 'step-connector' : ''}`}
              variants={fadeInUp}
              data-testid={`process-step-${index}`}
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{step.number}</span>
                </div>
              </div>
              <div className="pt-1">
                <h3 className="font-heading text-xl font-medium text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Why Personal Advice Section
const WhyPersonalSection = () => {
  const reasons = [
    {
      title: "Informationen gibt es viele – Einordnung nicht",
      description: "Apps und Portale zeigen dir Produkte, aber keine helfen dir bei der Frage: Was passt zu mir?"
    },
    {
      title: "Jede Situation ist individuell",
      description: "Deine Lebenssituation, deine Ziele, dein Risikoprofil – das lässt sich nicht standardisieren."
    },
    {
      title: "Verständnis statt Abschlussdruck",
      description: "Mir ist wichtig, dass du verstehst, warum etwas sinnvoll ist – nicht dass du schnell unterschreibst."
    },
  ];

  return (
    <AnimatedSection className="section-spacing bg-sage-50" id="warum" data-testid="why-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-foreground mb-6">
              Warum persönliche Beratung?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              In einer Welt voller Apps, Vergleichsportale und YouTube-Videos fragst du dich vielleicht: 
              Brauche ich überhaupt noch eine persönliche Beratung?
            </p>
            
            <motion.div 
              className="space-y-6"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {reasons.map((reason, index) => (
                <motion.div 
                  key={index}
                  className="flex gap-4"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">{reason.title}</h4>
                    <p className="text-muted-foreground">{reason.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-border/40">
            <MessageCircle className="w-12 h-12 text-primary mb-6" />
            <blockquote className="font-heading text-2xl text-foreground leading-relaxed mb-4">
              „Langfristig denken statt kurzfristig abschließen."
            </blockquote>
            <p className="text-muted-foreground">
              Mein Ansatz: Ich nehme mir Zeit für dich und deine Fragen. Gemeinsam entwickeln wir einen Plan, 
              den du verstehst und der zu deinem Leben passt.
            </p>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="font-medium text-foreground">Christopher Mein</p>
              <p className="text-sm text-muted-foreground">Freier Versicherungsmakler & Finanzanlagenvermittler</p>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    vorname: "",
    email: "",
    nachricht: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ vorname: "", email: "", nachricht: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuche es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection className="section-spacing bg-white" id="kontakt" data-testid="contact-section">
      <div className="section-container">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-foreground mb-6">
            Lass uns deine Situation unverbindlich besprechen
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Das Erstgespräch ist kostenlos und unverbindlich. Es entsteht keine Verpflichtung.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact Form */}
          <motion.div 
            className="bg-[#FDFAF7] p-8 rounded-2xl"
            variants={fadeInUp}
          >
            <h3 className="font-heading text-2xl font-medium text-foreground mb-6">Schreib mir eine Nachricht</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="vorname" className="text-foreground font-medium mb-2 block">Vorname</Label>
                <Input
                  id="vorname"
                  type="text"
                  value={formData.vorname}
                  onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                  placeholder="Dein Vorname"
                  required
                  className="bg-white border-border focus:border-primary focus:ring-primary/20 rounded-xl h-12"
                  data-testid="contact-vorname-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-foreground font-medium mb-2 block">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="deine@email.de"
                  required
                  className="bg-white border-border focus:border-primary focus:ring-primary/20 rounded-xl h-12"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="nachricht" className="text-foreground font-medium mb-2 block">Nachricht</Label>
                <Textarea
                  id="nachricht"
                  value={formData.nachricht}
                  onChange={(e) => setFormData({ ...formData, nachricht: e.target.value })}
                  placeholder="Erzähl mir kurz, wobei ich dir helfen kann..."
                  required
                  rows={5}
                  className="bg-white border-border focus:border-primary focus:ring-primary/20 rounded-xl resize-none"
                  data-testid="contact-nachricht-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
                <Mail className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </motion.div>
          
          {/* Contact Info & Calendly */}
          <motion.div 
            className="space-y-8"
            variants={fadeInUp}
          >
            <div className="bg-primary p-8 rounded-2xl text-white">
              <h3 className="font-heading text-2xl font-medium mb-6">Oder direkt einen Termin buchen</h3>
              <p className="text-white/80 mb-6 leading-relaxed">
                Du möchtest lieber gleich ein unverbindliches Kennenlerngespräch vereinbaren? 
                Buche direkt einen passenden Termin in meinem Kalender.
              </p>
              
              <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-white text-primary hover:bg-sage-50 rounded-full px-8 py-6 text-lg font-medium w-full"
                    data-testid="calendly-trigger-button"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Termin vereinbaren
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Termin buchen</DialogTitle>
                    <DialogClose className="absolute right-4 top-4">
                      <X className="w-5 h-5" />
                    </DialogClose>
                  </DialogHeader>
                  <div className="h-[600px] w-full">
                    <iframe
                      src={CALENDLY_LINK}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Calendly Booking"
                      data-testid="calendly-iframe"
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="bg-sage-50 p-8 rounded-2xl">
              <h3 className="font-heading text-xl font-medium text-foreground mb-6">Kontaktdaten</h3>
              
              <div className="space-y-4">
                <a 
                  href="tel:+4917681239688" 
                  className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors"
                  data-testid="phone-link"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <span>0176 81 23 9688</span>
                </a>
                
                <a 
                  href="mailto:kontakt@christopher-mein.de" 
                  className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors"
                  data-testid="email-link"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <span>kontakt@christopher-mein.de</span>
                </a>
                
                <div className="flex items-center gap-4 text-muted-foreground">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <span>Braunschweig & bundesweit digital</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Legal Notice
const LegalNotice = () => {
  return (
    <div className="bg-sage-50 py-6" data-testid="legal-notice">
      <div className="section-container">
        <p className="text-sm text-muted-foreground text-center">
          Die Inhalte dieser Website dienen der Orientierung und stellen keine Anlageberatung im rechtlichen Sinne dar.
        </p>
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-sage-800 text-white py-12" data-testid="footer">
      <div className="section-container">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <img 
              src={LOGO_URL} 
              alt="CM Consulting Logo" 
              className="h-12 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-sage-300 text-sm">
              CM Consulting – Christopher Mein<br />
              Freier Versicherungsmakler & Finanzanlagenvermittler<br />
              Braunschweig
            </p>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Schnellzugriff</h4>
            <div className="space-y-2">
              <a href="#leistungen" className="block text-sage-300 hover:text-white transition-colors text-sm">Leistungen</a>
              <a href="#ablauf" className="block text-sage-300 hover:text-white transition-colors text-sm">Ablauf</a>
              <a href="#kontakt" className="block text-sage-300 hover:text-white transition-colors text-sm">Kontakt</a>
            </div>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Rechtliches</h4>
            <div className="space-y-2">
              <Link to="/impressum" className="block text-sage-300 hover:text-white transition-colors text-sm" data-testid="impressum-link">
                Impressum (inkl. §34d / §34f)
              </Link>
              <Link to="/datenschutz" className="block text-sage-300 hover:text-white transition-colors text-sm" data-testid="datenschutz-link">
                Datenschutzerklärung
              </Link>
            </div>
          </div>
        </div>
        
        <div className="border-t border-sage-700 pt-8">
          <p className="text-sage-400 text-sm text-center">
            © {new Date().getFullYear()} CM Consulting – Christopher Mein. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Homepage component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <TrustSection />
      <ProblemSection />
      <SolutionSection />
      <ProcessSection />
      <WhyPersonalSection />
      <ContactSection />
      <LegalNotice />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-center" richColors />
        <CookieBanner />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
